/**
 * Anonymous NPS member feedback survey (/nps/:token).
 * @agentic members
 */

import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Center,
  Loader,
  Paper,
  Stack,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import type { Questionnaire } from "@medplum/fhirtypes";

import { submitNpsSurvey, validateNpsSurvey } from "./api";
import {
  CARE_TEAM_CONTACT_MESSAGE,
  surveyErrorToState,
} from "./surveyErrorState";
import { NpsScaleInput } from "./NpsScaleInput";
import { SurveyShell } from "./SurveyShell";
import { SurveyWordmark } from "./SurveyWordmark";

type NpsSurveyPageProps = {
  token: string | null;
};

/**
 * Simple message card for terminal NPS states, optionally with care-team guidance.
 * @agentic members
 */
const NpsMessageCard = ({
  message,
  contactCareTeam = false,
}: {
  message: string;
  contactCareTeam?: boolean;
}) => (
  <SurveyShell>
    <Paper p="xl" radius="lg">
      <Stack gap="sm">
        <Text>{message}</Text>
        {contactCareTeam && <Text fw={500}>{CARE_TEAM_CONTACT_MESSAGE}</Text>}
      </Stack>
    </Paper>
  </SurveyShell>
);

type PageState =
  | { status: "loading" }
  | { status: "invalid" }
  | { status: "expired" }
  | { status: "already-completed" }
  | { status: "error"; message: string }
  | {
      status: "form";
      welcomeTitle: string;
      welcomeBody: string;
      thankYouMessage: string;
      questionnaire: Questionnaire;
    }
  | { status: "submitting" }
  | { status: "thank-you"; message: string };

/**
 * NPS survey flow without birthday verification.
 * @agentic members
 */
export const NpsSurveyPage = ({ token }: NpsSurveyPageProps) => {
  const [state, setState] = useState<PageState>(
    token ? { status: "loading" } : { status: "invalid" },
  );
  const [score, setScore] = useState<string | undefined>();
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (!token) {
      return;
    }

    validateNpsSurvey(token)
      .then((data) =>
        setState({
          status: "form",
          welcomeTitle: data.welcomeTitle,
          welcomeBody: data.welcomeBody,
          thankYouMessage: data.thankYouMessage,
          questionnaire: data.questionnaire,
        }),
      )
      .catch((err) => {
        setState(surveyErrorToState(err, "This link is not valid."));
      });
  }, [token]);

  const handleSubmit = useCallback(async () => {
    if (!token || state.status !== "form" || score === undefined) {
      return;
    }

    setState({ status: "submitting" });

    const scoreItem = state.questionnaire.item?.find(
      (item) => item.linkId === "nps-score",
    );
    const canonicalCoding = scoreItem?.answerOption?.find(
      (option) => option.valueCoding?.code === score,
    )?.valueCoding;
    const valueCoding = {
      code: canonicalCoding?.code ?? score,
      display: canonicalCoding?.display ?? score,
    };

    const items: Parameters<typeof submitNpsSurvey>[1] = [
      {
        linkId: "nps-score",
        answer: [{ valueInteger: Number.parseInt(score, 10), valueCoding }],
      },
    ];
    const trimmed = comment.trim();
    if (trimmed.length > 0) {
      items.push({
        linkId: "nps-comment",
        answer: [{ valueString: trimmed }],
      });
    }

    try {
      const result = await submitNpsSurvey(token, items);
      setState({
        status: "thank-you",
        message: result.thankYouMessage,
      });
    } catch (err) {
      setState(surveyErrorToState(err, "Could not submit survey."));
    }
  }, [comment, score, state, token]);

  if (state.status === "loading" || state.status === "submitting") {
    return (
      <SurveyShell>
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </SurveyShell>
    );
  }

  if (state.status === "invalid") {
    return (
      <NpsMessageCard
        message="This survey link is not valid."
        contactCareTeam
      />
    );
  }

  if (state.status === "expired") {
    return (
      <NpsMessageCard
        message="This survey link has expired and can no longer be used."
        contactCareTeam
      />
    );
  }

  if (state.status === "already-completed") {
    return (
      <NpsMessageCard message="You have already completed this survey. Thank you." />
    );
  }

  if (state.status === "error") {
    return <NpsMessageCard message={state.message} />;
  }

  if (state.status === "thank-you") {
    return (
      <SurveyShell>
        <Paper p="xl" radius="lg">
          <Stack gap="md">
            <SurveyWordmark />
            <Title order={3}>{state.message}</Title>
          </Stack>
        </Paper>
      </SurveyShell>
    );
  }

  return (
    <SurveyShell>
      <Paper p="xl" radius="lg" shadow="sm">
        <Stack gap="lg">
          <SurveyWordmark />
          <Stack gap="xs">
            <Title order={2}>{state.welcomeTitle}</Title>
            <Text size="sm" c="dimmed">
              {state.welcomeBody}
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text fw={600}>
              On a scale of 0–10, how likely are you to recommend our mental
              health concierge service to a friend or family member?
            </Text>
            <NpsScaleInput value={score} onChange={setScore} />
          </Stack>

          <Textarea
            label="Optional: Could you share a bit about why you chose this score? What's one thing we can do better?"
            placeholder="Your feedback (optional)"
            minRows={3}
            value={comment}
            onChange={(e) => setComment(e.currentTarget.value)}
          />

          <Button
            size="md"
            disabled={score === undefined}
            onClick={() => {
              void handleSubmit();
            }}
          >
            Submit
          </Button>
        </Stack>
      </Paper>
    </SurveyShell>
  );
};
